﻿import { AlertCircle, CalendarDays, Loader2, MapPin, Plus, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { useState } from "react";
import type { EventStatus } from "@ticketnjoy/types";

import { DashboardPage } from "@/components/dashboard-page";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useEventsQuery } from "@/features/events/api";
import { formatEventDate } from "@/features/events/date-helpers";

const STATUS_LABELS: Record<EventStatus, string> = {
  Draft: "Taslak",
  Published: "Yayında",
  Paused: "Duraklatıldı",
  Cancelled: "İptal",
  Ended: "Sona Erdi",
  Archived: "Arşivde",
};

const STATUS_VARIANTS: Record<EventStatus, "success" | "warning" | "secondary" | "destructive" | "outline" | "default"> = {
  Draft: "outline",
  Published: "success",
  Paused: "warning",
  Cancelled: "destructive",
  Ended: "secondary",
  Archived: "secondary",
};

type FilterValue = EventStatus | "active";

const FILTERS: Array<{ label: string; value: FilterValue }> = [
  { label: "Aktif", value: "active" },
  { label: "Taslak", value: "Draft" },
  { label: "Yayında", value: "Published" },
  { label: "Duraklatıldı", value: "Paused" },
  { label: "Sona Erdi", value: "Ended" },
  { label: "İptal", value: "Cancelled" },
  { label: "Arşiv", value: "Archived" },
];

// "Aktif" görünümü: iptal ve arşiv dışındaki tüm statuslar (günlük çalışma akışı).
const ACTIVE_STATUSES: readonly EventStatus[] = [
  "Draft",
  "Published",
  "Paused",
  "Ended",
];

export function EventsListPage() {
  const [filter, setFilter] = useState<FilterValue>("active");
  // Backend tekil status filtresi destekliyor; "active" için tüm listeyi çekip client-side filtreliyoruz.
  const backendStatus = filter === "active" ? undefined : filter;
  const { data: rawData, isLoading } = useEventsQuery(backendStatus);
  const data =
    filter === "active" && rawData
      ? rawData.filter((e) => ACTIVE_STATUSES.includes(e.status))
      : rawData;

  return (
    <DashboardPage>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Etkinlikler</h1>
          <p className="text-sm text-muted-foreground">
            Tüm etkinliklerini burada yönet. Etkinlikler bağımsız — satışa sunmak için mağazalarına eklemelisin.
          </p>
        </div>
        <Button asChild>
          <Link to="/events/new">
            <Plus className="mr-1.5 h-4 w-4" /> Yeni Etkinlik
          </Link>
        </Button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? "default" : "outline"}
            size="sm"
            onClick={() => setFilter(f.value)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !data || data.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {data.map((evt) => (
            <Card key={evt.id} className="transition-colors hover:border-primary/40">
              <CardHeader className="space-y-2">
                <div className="flex items-center justify-between">
                  <CalendarDays className="h-5 w-5 text-muted-foreground" />
                  <Badge variant={STATUS_VARIANTS[evt.status]}>
                    {STATUS_LABELS[evt.status]}
                  </Badge>
                </div>
                <CardTitle className="line-clamp-2 text-lg">{evt.title}</CardTitle>
                <CardDescription className="flex items-center gap-1 text-xs">
                  <MapPin className="h-3 w-3" />
                  {evt.venueCity ?? "Mekan belirtilmedi"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm">
                  <div className="text-muted-foreground">Başlangıç</div>
                  <div className="font-medium">{formatEventDate(evt.startsAt)}</div>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Users className="h-3 w-3" />
                  {evt.totalSold}/{evt.totalCapacity || "—"} satıldı
                  <span>·</span>
                  {evt.ticketTypeCount} bilet türü
                </div>
                <Button variant="outline" size="sm" className="w-full" asChild>
                  <Link to={`/events/${evt.slug}`}>Yönet</Link>
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </DashboardPage>
  );
}

function EmptyState() {
  return (
    <Card>
      <CardHeader className="items-center text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-muted">
          <AlertCircle className="h-6 w-6 text-muted-foreground" />
        </div>
        <CardTitle>Henüz etkinliğin yok</CardTitle>
        <CardDescription>
          İlk etkinliğini oluştur, bilet türlerini tanımla, sonra bir mağazana ekleyerek satışa sun.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex justify-center">
        <Button asChild>
          <Link to="/events/new">
            <Plus className="mr-1.5 h-4 w-4" /> İlk Etkinlik
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
